import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  FileText,
  FolderKanban,
  GraduationCap,
  LibraryBig,
  Search,
  Sparkles,
  Trophy,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

const pageConfig = {
  "research-papers": {
    eyebrow: "Research Repository",
    title: "Research Papers",
    description:
      "Browse scholarly papers, abstracts, journal articles and research outputs from Federal Polytechnic Bida.",
    icon: FileText,
    gradient: "from-blue-950 via-indigo-900 to-slate-950",
    endpoint: "/api/journal-articles",
    fallback: [
      {
        id: "sample-research-paper",
        title: "Sample Research Paper",
        authors: "Federal Polytechnic Bida Research Team",
        abstract:
          "A sample research record. Replace this with live research articles from the Journal Articles CMS.",
        date: "Recently published",
        category: "Research Paper",
        href: "/polymath-journal",
      },
    ],
  },
  "student-projects": {
    eyebrow: "Student Innovation Archive",
    title: "Student Projects",
    description:
      "Explore student project works, innovation prototypes, final-year research outputs and practical solutions.",
    icon: GraduationCap,
    gradient: "from-emerald-950 via-teal-900 to-slate-950",
    endpoint: "/api/projects",
    fallback: [
      {
        id: "sample-student-project",
        title: "Sample Student Project",
        authors: "Student Research Group",
        abstract:
          "A sample student project record. Admin can later connect this page to a dedicated student project CMS.",
        date: "Academic Session",
        category: "Student Project",
        href: "/projects",
      },
    ],
  },
  "conference-proceedings": {
    eyebrow: "Academic Events Archive",
    title: "Conference Proceedings",
    description:
      "Access conference papers, workshop proceedings, seminar collections and institutional academic event records.",
    icon: Trophy,
    gradient: "from-purple-950 via-violet-900 to-slate-950",
    endpoint: "/api/journal-issues",
    fallback: [
      {
        id: "sample-conference-proceeding",
        title: "Sample Conference Proceeding",
        authors: "Academic Planning and Research Directorate",
        abstract:
          "A sample conference proceeding entry. Add real conference/proceeding records through the research or journal CMS.",
        date: "Conference Archive",
        category: "Conference Proceeding",
        href: "/polymath-journal",
      },
    ],
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

function normalizeRecord(item, type) {
  const isPaper = type === "research-papers";
  const isProject = type === "student-projects";
  return {
    id: item.id || item.slug || item.title,
    title: item.title || item.name || item.project_title || "Untitled record",
    authors:
      item.authors ||
      item.author ||
      item.department ||
      item.implementing_unit ||
      item.publisher ||
      (isProject ? "Student Project Team" : "Federal Polytechnic Bida"),
    abstract:
      item.abstract ||
      item.summary ||
      item.description ||
      "Open this record to view more information and related academic details.",
    date:
      item.publication_date ||
      item.published_at ||
      item.start_date ||
      item.created_at ||
      "Recently added",
    category:
      item.category ||
      item.type ||
      item.material_type ||
      (isPaper ? "Research Paper" : isProject ? "Student Project" : "Conference Proceeding"),
    href: item.slug
      ? isPaper
        ? `/journals/article/${item.slug}`
        : isProject
          ? `/projects/${item.slug}`
          : `/journals/${item.slug}`
      : isProject
        ? "/projects"
        : "/polymath-journal",
  };
}

function formatDate(value) {
  if (!value) return "Recently added";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function ResearchResourcePage({ type = "research-papers" }) {
  const config = pageConfig[type] || pageConfig["research-papers"];
  const Icon = config.icon || LibraryBig;
  const [records, setRecords] = useState(config.fallback);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetch(`${API_BASE}${config.endpoint}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!mounted) return;
        const rows = Array.isArray(data) ? data.map((item) => normalizeRecord(item, type)) : [];
        setRecords(rows.length ? rows : config.fallback);
      })
      .catch(() => {
        if (mounted) setRecords(config.fallback);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [config.endpoint, type]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return records;
    return records.filter((item) =>
      `${item.title} ${item.authors} ${item.abstract} ${item.category}`.toLowerCase().includes(needle)
    );
  }, [records, query]);

  return (
    <main className="min-h-screen bg-slate-50 text-slate-950">
      <section className={`relative overflow-hidden bg-gradient-to-br ${config.gradient} text-white`}>
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="absolute -right-24 top-10 h-80 w-80 rounded-full bg-white/10 blur-3xl"
        />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="max-w-4xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.24em] text-white/80 backdrop-blur">
              <Icon className="h-4 w-4" /> {config.eyebrow}
            </span>
            <h1 className="mt-6 text-5xl font-black tracking-tight md:text-6xl">{config.title}</h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-white/80">{config.description}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-10 grid gap-4 rounded-[2rem] border border-white/10 bg-white/10 p-4 backdrop-blur lg:grid-cols-[1fr_auto]"
          >
            <div className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 text-slate-900">
              <Search className="h-5 w-5 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${config.title.toLowerCase()}...`}
                className="w-full bg-transparent text-sm font-semibold outline-none placeholder:text-slate-400"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl bg-white/10 px-4 py-3 text-center">
                <p className="text-xs font-bold text-white/60">Records</p>
                <strong className="text-2xl font-black">{records.length}</strong>
              </div>
              <div className="rounded-2xl bg-white/10 px-4 py-3 text-center">
                <p className="text-xs font-bold text-white/60">Visible</p>
                <strong className="text-2xl font-black">{filtered.length}</strong>
              </div>
              <div className="rounded-2xl bg-white/10 px-4 py-3 text-center">
                <p className="text-xs font-bold text-white/60">Source</p>
                <strong className="text-sm font-black">CMS</strong>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
        <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-blue-700">
              <Sparkles className="h-4 w-4" /> Academic Resources
            </p>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950">Browse {config.title}</h2>
          </div>
          <p className="text-sm font-black text-slate-500">{loading ? "Loading..." : `${filtered.length} item(s) found`}</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((item, index) => (
            <motion.article
              key={`${item.id}-${index}`}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: index * 0.04 }}
              whileHover={{ y: -7 }}
              className="group rounded-[1.7rem] border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-2xl hover:shadow-slate-200"
            >
              <div className="mb-5 flex items-center justify-between gap-4">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                  <BookOpen className="h-6 w-6" />
                </span>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-600">{item.category}</span>
              </div>
              <h3 className="line-clamp-2 text-xl font-black leading-tight text-slate-950">{item.title}</h3>
              <p className="mt-2 text-sm font-bold text-slate-500">{item.authors}</p>
              <p className="mt-4 line-clamp-4 text-sm leading-7 text-slate-600">{item.abstract}</p>
              <div className="mt-5 flex items-center justify-between gap-4 border-t border-slate-100 pt-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500">
                  <CalendarDays className="h-4 w-4" /> {formatDate(item.date)}
                </span>
                <Link to={item.href} className="inline-flex items-center gap-2 text-sm font-black text-blue-700 hover:text-blue-900">
                  Open <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>

        {!loading && filtered.length === 0 && (
          <div className="rounded-[2rem] border border-slate-200 bg-white p-10 text-center shadow-sm">
            <FolderKanban className="mx-auto mb-4 h-12 w-12 text-blue-600" />
            <h3 className="text-2xl font-black text-slate-950">No records found</h3>
            <p className="mt-2 text-slate-600">Try another keyword or check again after admin uploads records.</p>
          </div>
        )}
      </section>
    </main>
  );
}
